<template>
  <div class="fixed top-4 right-4 z-[9999] max-w-md w-full mx-4 md:mx-0 space-y-3 pointer-events-none">
    <TransitionGroup name="notification-list">
      <div 
        v-for="notification in notifications" 
        :key="notification.id"
        class="pointer-events-auto"
      >
        <Transition name="notification">
          <div 
            v-if="notification.visible" 
            :class="[
              'rounded-xl shadow-2xl p-4 flex items-start gap-3 border-l-4',
              typeClasses[notification.type]
            ]"
            class="backdrop-blur-sm"
          >
            <div class="flex-shrink-0 mt-0.5">
              <div 
                :class="[
                  'w-10 h-10 rounded-full flex items-center justify-center',
                  iconBgClasses[notification.type]
                ]"
              >
                <svg 
                  v-if="notification.type === 'error'" 
                  class="w-5 h-5 text-white" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path 
                    stroke-linecap="round" 
                    stroke-linejoin="round" 
                    stroke-width="2" 
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
                
                <svg 
                  v-else-if="notification.type === 'success'" 
                  class="w-5 h-5 text-white" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path 
                    stroke-linecap="round" 
                    stroke-linejoin="round" 
                    stroke-width="2" 
                    d="M5 13l4 4L19 7"
                  />
                </svg>
                
                <svg 
                  v-else-if="notification.type === 'warning'" 
                  class="w-5 h-5 text-white" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path 
                    stroke-linecap="round" 
                    stroke-linejoin="round" 
                    stroke-width="2" 
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
                
                <svg 
                  v-else 
                  class="w-5 h-5 text-white" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path 
                    stroke-linecap="round" 
                    stroke-linejoin="round" 
                    stroke-width="2" 
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
            </div>

            <div class="flex-1 pt-0.5">
              <h4 :class="titleClasses[notification.type]" class="font-semibold text-sm mb-1">
                {{ notification.title }}
              </h4>
              <p class="text-gray-600 text-sm leading-relaxed">
                {{ notification.message }}
              </p>
            </div>

            <button 
              @click="close(notification.id)"
              class="flex-shrink-0 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>
        </Transition>
      </div>
    </TransitionGroup>
  </div>
</template>

<script setup>
import { useNotification } from '../composables/useNotification'

const { notifications, close } = useNotification()

const typeClasses = {
  success: 'bg-white border-green-500',
  error: 'bg-white border-red-500',
  warning: 'bg-white border-yellow-500',
  info: 'bg-white border-blue-500'
}

const iconBgClasses = {
  success: 'bg-green-500',
  error: 'bg-red-500',
  warning: 'bg-yellow-500',
  info: 'bg-blue-500'
}

const titleClasses = {
  success: 'text-green-800',
  error: 'text-red-800',
  warning: 'text-yellow-800',
  info: 'text-blue-800'
}
</script>

<style scoped>
.notification-enter-active,
.notification-leave-active {
  transition: all 0.3s ease;
}

.notification-enter-from {
  opacity: 0;
  transform: translateX(100px);
}

.notification-leave-to {
  opacity: 0;
  transform: translateX(100px);
}

.notification-list-move,
.notification-list-enter-active,
.notification-list-leave-active {
  transition: all 0.3s ease;
}

.notification-list-enter-from,
.notification-list-leave-to {
  opacity: 0;
  transform: translateX(30px);
}

.notification-list-leave-active {
  position: absolute;
}
</style>
